
import { Component, Vue, Prop, Watch } from 'vue-property-decorator'
import { Scene } from '@babylonjs/core/scene'
import { Mesh } from '@babylonjs/core/Meshes/mesh'
import { Color3 } from '@babylonjs/core/Maths/math.color'
import { ArcRotateCamera } from '@babylonjs/core/Cameras/arcRotateCamera'
import { CubeTexture } from '@babylonjs/core/Materials/Textures/cubeTexture'

import { PointerEventTypes, PointerInfo } from '@babylonjs/core/Events/pointerEvents'
import { Animatable } from '@babylonjs/core/Animations'
import { zoomOnCamera } from '@/lib/zoomOnCamera'
import { centerMesh } from '@/lib/centerMesh'
import {
  createDefaultCameraResetAnimation,
  resetCameraView,
  restorePreviousState,
  start3DRotateAnimation
} from '@/lib/camera'
import { getFallbackUrl } from '@/lib/modelSizeFallback'
import { RESOURCESURL } from '@/lib/const'
import eventHub from '@/lib/eventHub'

type Query = {
  debug: boolean,
  model: string,
  mode: string
}
@Component({
})
export default class Viewport extends Vue {
  @Prop({ type: Object, default: () => ({}) })
  query!: Query

  baseModel: Mesh | null = null
  scene: Scene | null = null
  rgxHide = /^(coll_|pivot|hotspot|ref_dist|area)/
  clearColor = Color3.FromHexString('#fcfcfc').toColor4(1.0)
  clear3dModeColor = Color3.FromHexString('#f4f4f4').toColor4(1.0)
  modelUrl = ''
  mode: string | null = null
  assetLoadTout = 0
  modeAnimation: Animatable | null = null
  animationStarted = true
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  initCameraState: any | null = null

  @Watch('query.model')
  async getModelUrl () {
    const url = await getFallbackUrl(`${RESOURCESURL}/models/${this.query.model}`)
    this.modelUrl = url
  }

  @Watch('query.mode')
  async getMode () {
    this.mode = this.query.mode
    if (this.scene != null) {
      this.scene.clearColor = this.mode === '3d' ? this.clear3dModeColor : this.clearColor
    }
  }

  onScenePointer (pointerInfo: PointerInfo) {
    if (this.mode !== '3d' || this.modeAnimation == null || this.scene == null) {
      return
    }
    switch (pointerInfo.type) {
      case PointerEventTypes.POINTERUP:
      case PointerEventTypes.POINTERWHEEL:
        if (this.animationStarted) {
          this.modeAnimation.pause()
          this.animationStarted = false
        }
        break
      default:
        break
    }
  }

  onSceneReady (scene: Scene) {
    this.scene = scene
    this.scene.onPointerObservable.add(this.onScenePointer)
    scene.clearColor = this.clearColor
    this.addEnvironmentTexture(scene)
  }

  setCameraProperties (camera: ArcRotateCamera) {
    camera.wheelDeltaPercentage = 0.03
    camera.panningSensibility = 100000
    camera.panningInertia = 0.001
    camera.pinchDeltaPercentage = 0.001
    camera.maxZ = 500
    camera.lowerRadiusLimit = 0.015
    if (this.mode === '3d') {
      camera.radius *= 1.5
      camera.upperRadiusLimit = camera.radius * 5
      camera.lowerRadiusLimit = camera.radius / 3.0
      camera.minZ = 0.001
    }
  }

  addEnvironmentTexture (scene: Scene) {
    const texture = CubeTexture.CreateFromPrefilteredData('textures/environment.env', scene)
    texture.rotationY = 4.7
    scene.environmentTexture = texture
    scene.environmentIntensity = 1
  }

  @Watch('baseModel')
  onBaseModelMaybeLoaded () {
    // this will fire twice, the first one being bogus
    if (this.baseModel?.getChildren()?.length && this.scene) {
      this.onBaseModelLoaded()
    } else {
      // this because Asset doesn't emit fail event
      // https://github.com/Beg-in/vue-babylonjs/issues/64#issuecomment-926486626
      this.assetLoadTout = setTimeout(() => {
        this.$emit('fail')
      }, 5000)
    }
  }

  onBaseModelLoaded () {
    if (!this.scene || !this.baseModel || !this.scene.activeCamera) return
    clearTimeout(this.assetLoadTout)

    const camera = this.scene.activeCamera as ArcRotateCamera
    this.scene.imageProcessingConfiguration.toneMappingEnabled = false
    this.baseModel.getChildren(x => !!x.name.match(this.rgxHide), false).forEach(x => { x.setEnabled(false) })
    centerMesh(this.baseModel)
    zoomOnCamera(this.baseModel, camera)
    this.setCameraProperties(camera as ArcRotateCamera)
    createDefaultCameraResetAnimation(camera)
    this.$emit('load')

    if (this.mode === '3d') {
      // Start 360 degree rotation
      this.modeAnimation = start3DRotateAnimation(this.scene, this.baseModel, 20)

      this.initCameraState = {
        target: camera.target,
        radius: camera.radius,
        alpha: camera.alpha,
        beta: camera.beta
      }
    }
  }

  onResetCamera () {
    if (!this.scene) return
    resetCameraView(this.scene.activeCamera as ArcRotateCamera)
  }

  mounted () {
    eventHub.$on('reset-camera', this.onResetCamera)
    window.addEventListener('mouseout', () => {
      if (this.mode !== '3d' || this.modeAnimation == null || this.scene == null || this.animationStarted) {
        return
      }

      // Resume the rotate animation
      // Start 360 degree rotation
      restorePreviousState(this.scene, this.initCameraState)
      this.modeAnimation.restart()
      this.animationStarted = true
    })
  }
}
